import { ArrowForwardIosRounded } from '@mui/icons-material';
import { Box, Button, styled } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import React, { useCallback, useMemo } from 'react';

import { StyledSkeleton } from '../../../theme/CustomComponents';
import { DataRequestItem } from '../../../Types/dealGPT';
import { PaginationMetadata } from '../../../Types/pagination';
import { useDrawer } from '../../Providers/DrawerProvider';
import DataRequestItemDrawer from './DataRequestItemDrawer';
import { DataRequestItemPrioritySelect } from './DataRequestItemPrioritySelect';
import { DataRequestItemStatusSelect } from './DataRequestItemStatusSelect';

const ActionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '100%',
  minWidth: 0,
  border: 'none',
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderRadius: 0,
  padding: 0.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
}));

interface DataRequestListTableProps {
  data: DataRequestItem[] | null;
  pagination: PaginationMetadata | null;
  setPagination: (page: number, pageSize: number) => void;
  refetchData: () => Promise<void>;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
}

const DataRequestListTable = ({
  data,
  pagination,
  setPagination,
  refetchData,
  sortModel,
  onSortModelChange,
}: DataRequestListTableProps): JSX.Element => {
  const { openDrawer } = useDrawer();

  const columns = useMemo<GridColDef<DataRequestItem>[]>(
    () => [
      {
        field: 'key',
        headerName: 'No.',
      },
      {
        field: 'scope',
        headerName: 'Scope',
        minWidth: 120,
      },
      {
        field: 'details',
        headerName: 'Details',
        minWidth: 200,
        flex: 1,
        renderCell: (params) => (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{params.value}</div>
        ),
      },
      {
        field: 'priority',
        headerName: 'Priority',
        minWidth: 150,
        renderCell: ({ row }) => (
          <DataRequestItemPrioritySelect item={row} onItemChanged={refetchData} />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        renderCell: ({ row }) => (
          <DataRequestItemStatusSelect item={row} onItemChanged={refetchData} />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        width: 30,
        renderCell: ({ row }) => (
          <ActionButton
            onClick={() =>
              openDrawer({
                anchor: 'right',
                size: 'sm',
                content: <DataRequestItemDrawer item={row} refetchItems={refetchData} />,
              })
            }
          >
            <ArrowForwardIosRounded fontSize="small" sx={{ color: '#070154' }} />
          </ActionButton>
        ),
      },
    ],
    [openDrawer, refetchData]
  );

  const loadingOverlay = useCallback(() => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
        {[...Array(5)].map((_, rowIndex) => (
          <Box key={rowIndex} sx={{ display: 'flex', gap: 1 }}>
            {columns.map((col) => {
              const skeletonStyle = col.width
                ? { width: col.width }
                : col.flex
                ? { flex: col.flex, minWidth: col.minWidth || 80 }
                : { minWidth: col.minWidth || 80 };
              return (
                <StyledSkeleton
                  key={col.field}
                  variant="rectangular"
                  sx={{ ...skeletonStyle }}
                  height={45}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }, [columns]);

  return (
    <DataGrid
      rows={data ?? []}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={pagination?.total ?? 0}
      sortingMode="server"
      paginationMode="server"
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableRowSelectionOnClick
      filterDebounceMs={300}
      getRowHeight={() => 'auto'}
      slots={{ loadingOverlay }}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      paginationModel={{
        page: pagination ? pagination.page : 0,
        pageSize: pagination ? pagination.pageSize : 10,
      }}
      pageSizeOptions={[10, 25, 50]}
      onPaginationModelChange={({ page, pageSize }) => setPagination(page, pageSize)}
      sx={{
        '& .MuiDataGrid-row': {
          backgroundColor: 'transparent',

          // kill hover
          '&:hover, &.Mui-hovered': {
            backgroundColor: 'transparent',
          },
        },

        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
          p: 2,

          // inline field-specific override
          '&[data-field="actions"]': {
            p: 0,
          },
        },
      }}
    />
  );
};

export default DataRequestListTable;
